import type { DialogProps } from "@radix-ui/react-dialog";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import {
  Dialog,
  DialogContent,
} from "@v1/components/designSystem/shadcn/core/dialog";
import { Command as CommandPrimitive } from "cmdk";

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    filter={(value, search) => (value?.includes(search) ? 1 : 0)}
    ref={ref}
    className={className}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

interface CommandDialogProps extends DialogProps {}

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0 shadow-lg flex flex-col">
        <Command className="flex flex-col [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    startEnhancer?: React.ReactNode;
    endEnhancer?: React.ReactNode;
    variant?: "sm" | "base";
    outlineOnActive?: boolean;
  }
>(
  (
    {
      className,
      startEnhancer,
      endEnhancer,
      variant = "sm",
      outlineOnActive = true,
      ...props
    },
    ref,
  ) => (
    <div className="flex items-center">
      <div
        className={twMerge(
          "bg-sheet border-0.5 border-component-border flex w-full items-center gap-2 rounded-md",
          outlineOnActive &&
            "focus-within:ring-primary focus-within:ring-1 focus-within:ring-inset focus-within:ring-offset-0 focus-within:ring-offset-transparent",
          variant === "sm" ? "h-6" : "h-8",
        )}
      >
        {startEnhancer && (
          <div className="text-muted ml-2">{startEnhancer}</div>
        )}
        <CommandPrimitive.Input
          ref={ref}
          className={twMerge(
            "placeholder:text-placeholder outline-hidden flex h-full w-full rounded-md bg-transparent text-sm placeholder:font-normal disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:ring-0",
            variant === "sm" ? "text-xs" : "text-sm",
            className,
          )}
          {...props}
        />
        {endEnhancer && <div className="mr-2">{endEnhancer}</div>}
      </div>
    </div>
  ),
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={twMerge(
      // Note (Noah, 2024-11-03, REPL-14468): vh value here is arbitrary,
      // but 56vh makes the dropdown show overflow for items on a Macbook Pro
      // 13", which is nice to indicate that there are more items
      "max-h-[56vh] overflow-y-auto overflow-x-hidden",
      className,
    )}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="text-default p-3 text-xs"
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={twMerge(
      "overflow-hidden py-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-items]]:flex [&_[cmdk-group-items]]:flex-col [&_[cmdk-group-items]]:gap-[1px]",
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={twMerge("bg-surface -mx-1 h-px", className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
    variant?: "sm" | "base";
  }
>(({ className, variant = "sm", ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={twMerge(
      "outline-hidden relative flex cursor-default select-none items-center truncate rounded-sm data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
      variant === "sm" ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm",
      className,
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={twMerge("ml-auto text-xs tracking-widest", className)}
      {...props}
    />
  );
};
CommandShortcut.displayName = "CommandShortcut";

/** @public */
export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
